import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Secciones/Header';
import Footer from '../components/Secciones/Footer';
import { UserAuthContext } from '../UserAuthContext';
import { getProductImageUrl, useProductImageFallback } from '../utils/productImages';
import './UserOrderDetails.css'; // Updated to use dedicated CSS

const UserOrderDetails = () => {
  const { user, loading: authLoading } = useContext(UserAuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!user || authLoading) {
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('user_token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await axios.get(`https://valleyapi-dr9y.onrender.com/api/cart/orders/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const parsedOrder = {
          ...response.data,
          subtotal: parseFloat(response.data.subtotal),
          shipping_cost: parseFloat(response.data.shipping_cost),
          taxes: parseFloat(response.data.taxes),
          total: parseFloat(response.data.total),
          items: response.data.items
            ? response.data.items.map((item) => ({
                ...item,
                product_price: parseFloat(item.product_price),
                product_image: getProductImageUrl(item.product_image),
              }))
            : [],
        };
        setOrder(parsedOrder);
        setLoading(false);
      } catch (err) {
        console.error('❌ Error fetching order details:', err);
        setError(
          err.response?.status === 401
            ? 'Unauthorized: Please log in again.'
            : err.response?.status === 404
            ? 'Order not found.'
            : 'Failed to fetch order details. Please try again later.'
        );
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [user, authLoading, id]);

  const handleCheckout = () => {
    navigate(`/checkout/${id}`);
  };

  const handleAskForUpdates = () => {
    navigate(`/orders/${id}/updates`);
  };

  if (authLoading || loading) {
    return (
      <div className="user-order-details-container">
        <Header />
        <div className="order-details-content">
          <h1>Loading...</h1>
        </div>
        <Footer />
      </div>
    );
  }

  if (!user) {
    navigate('/signup');
    return null;
  }

  if (error) {
    return (
      <div className="user-order-details-container">
        <Header />
        <div className="order-details-content">
          <h1>Error</h1>
          <p>{error}</p>
          <Link to="/orders" className="back-button">
            Back to Orders
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="user-order-details-container">
        <Header />
        <div className="order-details-content">
          <h1>Order Not Found</h1>
          <p>The requested order could not be found.</p>
          <Link to="/orders" className="back-button">
            Back to Orders
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="user-order-details-container">
      <Header />
      <div className="order-details-content">
        <h1>Order Details #{order.idorders}</h1>
        <div className="order-details-layout">
          <div className="order-items-card">
            <h2>Order Items</h2>
            {Array.isArray(order.items) && order.items.length > 0 ? (
              <div className="order-items-grid">
                {order.items.map((item) => (
                  <div key={item.idorder_item} className="order-item-card">
                    <img
                      src={item.product_image}
                      alt={item.product_name}
                      className="order-item-image"
                      onError={useProductImageFallback}
                    />
                    <div className="order-item-details">
                      <p><strong>Name:</strong> {item.product_name}</p>
                      <p><strong>Brand:</strong> {item.product_brand}</p>
                      <p><strong>Description:</strong> {item.product_description}</p>
                      <p>
                        <strong>Price:</strong> $
                        {isNaN(item.product_price) ? 'N/A' : item.product_price.toFixed(2)}
                      </p>
                      <p><strong>Quantity:</strong> {item.quantity}</p>
                      <p><strong>More Details:</strong> {item.More_Details || 'N/A'}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-items">No items found for this order.</p>
            )}
          </div>
          <div className="order-details-card">
            <h2>Order Information</h2>
            <div className="order-info-grid">
              <div className="order-info-item">
                <span className="order-info-label">Order ID:</span>
                <span>{order.idorders}</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-label">Company Name:</span>
                <span>{order.companyName}</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-label">Status:</span>
                <span className={`order-status ${order.order_state.toLowerCase().replace(/\s/g, '-')}`}>
                  {order.order_state}
                </span>
              </div>
              <div className="order-info-item">
                <span className="order-info-label">Created On:</span>
                <span>{new Date(order.date).toLocaleDateString()}</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-label">Delivery Date:</span>
                <span>{order.delivery_time ? new Date(order.delivery_time).toLocaleDateString() : 'N/A'}</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-label">Billing Address:</span>
                <span>{order.billing_address}</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-label">Shipping Address:</span>
                <span>{order.shipping_address}</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-label">PO Number:</span>
                <span>{order.PO_Number || 'N/A'}</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-label">More Details:</span>
                <span>{order.More_Details || 'N/A'}</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-label">Subtotal:</span>
                <span>${isNaN(order.subtotal) ? 'N/A' : order.subtotal.toFixed(2)}</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-label">Shipping Cost:</span>
                <span>${isNaN(order.shipping_cost) ? 'N/A' : order.shipping_cost.toFixed(2)}</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-label">Taxes:</span>
                <span>${isNaN(order.taxes) ? 'N/A' : order.taxes.toFixed(2)}</span>
              </div>
              <div className="order-info-item">
                <span className="order-info-label">Total:</span>
                <span className="order-total">${isNaN(order.total) ? 'N/A' : order.total.toFixed(2)}</span>
              </div>
              {order.payment_id && (
                <div className="order-info-item">
                  <span className="order-info-label">Payment ID:</span>
                  <span>{order.payment_id}</span>
                </div>
              )}
            </div>
            <div className="order-actions">
              {order.order_state === 'Waiting for Payment' && (
                <button onClick={handleCheckout} className="action-button checkout-button">
                  Proceed to Checkout
                </button>
              )}
              <Link to="/orders" className="action-button back-button">
                Back to Orders
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default UserOrderDetails;
